import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Compute integer age (years) from a MM/DD/YYYY date-of-birth string. Returns
// null if the string isn't a valid MM/DD/YYYY date. Used for age-based
// eligibility gates (default intake floor is 18; sermorelin/tesamorelin
// require 20+ per Beluga policy 2026-06-17 — see
// docs/BACKEND_GH_THERAPY_AGE20_PRD.md).
export function computeAgeFromDob(dob) {
  if (typeof dob !== 'string') return null;
  const m = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const now = new Date();
  let age = now.getFullYear() - year;
  // Subtract a year if this year's birthday hasn't occurred yet.
  if (now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day)) {
    age -= 1;
  }
  return age;
}

// Minimum age for growth-hormone-pathway therapies (sermorelin, tesamorelin)
// per Beluga clinical policy (Nikka, 2026-06-17). Default intake floor is 18.
export const GH_THERAPY_MIN_AGE = 20;

// Where patients land after a successful intake submission — the patient
// dashboard login (Auth0 universal login lives behind it). 2026-06-29.
export const DASHBOARD_LOGIN_URL = 'https://dashboard.fitpal.com/login';

// Turn a failed Square payment (BE /payments/square-payment-create-payment
// error) into a patient-friendly, actionable message. The BE returns
// { msg, error } where `error` is a string like "Status code: 400\nBody: {json}"
// containing Square's errors[].code (e.g. CVV_FAILURE, TRANSACTION_LIMIT). We
// parse those codes and map the most actionable one. Falls back to a generic
// retry message. (2026-06-27 launch — replaces the raw "Square payment failed".)
const SQUARE_DECLINE_MESSAGES = {
  CVV_FAILURE: "Your card's security code (CVV) was incorrect. Please re-enter your card details and try again.",
  VERIFY_CVV_FAILURE: "Your card's security code (CVV) couldn't be verified. Please re-enter your card details and try again.",
  ADDRESS_VERIFICATION_FAILURE: "Your billing ZIP/postal code didn't match your card. Please re-enter it and try again.",
  VERIFY_AVS_FAILURE: "Your billing ZIP/postal code didn't match your card. Please re-enter it and try again.",
  INVALID_EXPIRATION: "Your card's expiration date looks invalid. Please check it and try again.",
  CARD_EXPIRED: "Your card has expired. Please try a different card.",
  INSUFFICIENT_FUNDS: "Your card was declined for insufficient funds. Please try a different card.",
  TRANSACTION_LIMIT: "Your card was declined — it may have hit a transaction limit or have insufficient available funds. Please try a different card or contact your bank.",
  PAYMENT_LIMIT_EXCEEDED: "This payment exceeded the card's limit. Please try a different card or contact your bank.",
  CARD_DECLINED_CALL_ISSUER: "Your card was declined — please contact your bank, or try a different card.",
  CARD_DECLINED_VERIFICATION_REQUIRED: "Your bank needs to verify this charge. Please contact your bank, or try a different card.",
  CARD_DECLINED: "Your card was declined. Please try a different card.",
  GENERIC_DECLINE: "Your card was declined. Please try a different card.",
  CARD_NOT_SUPPORTED: "This card type isn't supported. Please try a different card.",
  INVALID_CARD: "Your card details appear to be invalid. Please re-enter them and try again.",
  INVALID_CARD_DATA: "We couldn't process your card. Please re-enter your details and try again.",
  TEMPORARY_ERROR: "We hit a temporary issue processing your card. Please try again in a moment.",
};

// Square codes that mean "the request was wrong", not "the card was refused".
// A zero-amount CreatePayment (100%-off coupon) lands here.
const NON_CARD_CODES = new Set([
  'INVALID_VALUE', 'VALUE_TOO_LOW', 'VALUE_TOO_HIGH', 'BAD_REQUEST',
  'MISSING_REQUIRED_PARAMETER', 'INVALID_REQUEST_ERROR', 'CONFLICT',
  'UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND',
  'INTERNAL_SERVER_ERROR', 'SERVICE_UNAVAILABLE', 'GATEWAY_TIMEOUT',
]);

export function describeSquareFailure(error) {
  const data = error?.response?.data || {};
  let codes = [];
  if (Array.isArray(data.errors)) codes = data.errors.map((e) => e && e.code).filter(Boolean);
  if (!codes.length && typeof data.error === 'string') {
    const i = data.error.indexOf('{');
    if (i !== -1) {
      try {
        const parsed = JSON.parse(data.error.slice(i));
        if (Array.isArray(parsed.errors)) codes = parsed.errors.map((e) => e && e.code).filter(Boolean);
      } catch (_) { /* not JSON — ignore */ }
    }
  }
  // Prefer the most patient-actionable code when Square returns several.
  // IMPORTANT: funds/limit/issuer declines rank ABOVE CVV/AVS. Issuers often
  // tack on a spurious CVV_REJECTED when they decline for funds/limit, so a
  // CVV message would falsely blame the patient's security code. CVV/AVS only
  // win when they're the SOLE reason. (Observed live 2026-06-27: a debit card
  // declined for limit returned TRANSACTION_LIMIT + CVV_FAILURE though the CVV
  // was correct.)
  const priority = [
    'INSUFFICIENT_FUNDS', 'TRANSACTION_LIMIT', 'PAYMENT_LIMIT_EXCEEDED',
    'CARD_DECLINED_CALL_ISSUER', 'CARD_DECLINED_VERIFICATION_REQUIRED', 'CARD_DECLINED',
    'GENERIC_DECLINE', 'CARD_NOT_SUPPORTED', 'CARD_EXPIRED', 'INVALID_EXPIRATION',
    'CVV_FAILURE', 'VERIFY_CVV_FAILURE', 'ADDRESS_VERIFICATION_FAILURE', 'VERIFY_AVS_FAILURE',
    'INVALID_CARD', 'INVALID_CARD_DATA', 'TEMPORARY_ERROR',
  ];
  const chosen = priority.find((c) => codes.includes(c)) || codes[0];
  if (chosen && SQUARE_DECLINE_MESSAGES[chosen]) return SQUARE_DECLINE_MESSAGES[chosen];
  // NOT a card decline: either Square returned no decline code at all (a BE
  // 4xx/5xx, a network failure) or it returned a request-validation code. The
  // old copy said "check your card details … or use a different card" for
  // these, which is actively harmful — it sends the patient through card after
  // card for a problem no card can fix. Observed live 2026-08-20: a 100%-off
  // coupon produces a $0 total, Square's Payments API rejects a zero-amount
  // CreatePayment as a request error (no decline code), and every card the
  // patient tried returned this identical message.
  if (!chosen || NON_CARD_CODES.has(chosen)) {
    return "We couldn't complete your order. This isn't a problem with your card — trying a different card won't help. Please contact support and we'll get it sorted.";
  }
  return 'Your payment could not be processed. Please try again, or use a different card.';
}

// Resolve an option-bearing slide answer (consent-dropdown / single-select /
// multi-select) to its full option label(s) for the Beluga payload. Beluga
// requires the answer field to match the option label verbatim — e.g.
// "None of these apply to me" instead of the slug "none", and the full
// consent attestation instead of the value "consent" (visit 86529344
// flagged consent-dropdown; visit 53082431 flagged multi-select). Returns
// null when the slide has no options array so callers can fall through.
//
// `separator` joins multi-select (array) labels. Defaults to ', '. The bullet
// flows (trt/trtFollowup/womenSexualHealth/womenSexualHealthFollowup/
// mensSexualHealth) pass '; ' so the backend's count-validated split
// (controllers/Beluga.js — tries '; ' then ', ', accepts only a split yielding
// exactly rawAnswer.length parts) can bullet options whose labels themselves
// contain ', ' (e.g. nitrate-meds / alpha-blocker drug lists). Non-bullet flows
// keep ', ' — their multi-selects were approved comma-joined. Bulleting itself
// lives in the backend; the FE only sends the joined label string. Per Erika
// 2026-06-04 (WSH visit 11849568, trt 79331593) + backend eb31b20.
export function resolveOptionLabel(slide, answer, separator = ', ') {
  if (!slide || !Array.isArray(slide.options)) return null;
  const labelFor = (val) => {
    const opt = slide.options.find(o => o.value === val);
    return opt && typeof opt.label === 'string' ? opt.label : val;
  };
  if (Array.isArray(answer)) {
    return answer.map(labelFor).join(separator);
  }
  // consent-dropdown with a checkbox stores { option, checkboxChecked }
  const value = (answer && typeof answer === 'object' && 'option' in answer) ? answer.option : answer;
  if (typeof value !== 'string') return null;
  return labelFor(value);
}

// Backwards-compatible alias kept for the consent-only callsite naming.
export const consentAnswerLabel = resolveOptionLabel;

// Build a user-facing message for /beluga/create-visit-v2 and /followup/submit
// failures. Backend auto-refunds the captured Square charge on 4xx/5xx and
// returns `{refunded: true, refundId}` (or `{refunded: false, refundReason}`
// when the helper couldn't locate the transaction). We surface that state so
// the patient sees "your payment was refunded" instead of the legacy panic
// "your card was charged" copy. See BACKEND_FOLLOWUP_SUBMIT_PRD.md §3.3.
// Coded rejections from the practice's own (non-Beluga) concierge intake —
// `{ code, message, field?, reason? }` per BE PRs #318/#320/#321. The
// eligibility ones are enforced client-side first, so these mostly surface
// when a saved answer is edited after checkout or the served-state list moved.
const CODED_SUBMIT_ERRORS = {
  state_not_served: "we're not able to offer this program in your state yet",
  state_mismatch: 'the state on your address must match your state of residence',
  under_18: 'you must be 18 or older to join this program',
  underage: 'you must be 18 or older to join this program',
  not_eligible: "based on your answers, this program isn't a fit right now",
  plan_program_mismatch: "the selected plan doesn't match this program. Please refresh and try again",
  missing_consent: 'every consent step must be accepted before submitting',
  payment_required: 'your membership payment did not complete',
  payment_not_found: "we couldn't find an active membership for this email address",
  // §branded-refill (/followup/submit, quote path). Each auto-refunds; the BE
  // sends refunded:true|false + refundId, which the refund line below reads.
  quote_mismatch: "the payment didn't match the refill quote. Please reopen the refill from your dashboard",
  pharmacy_required: 'a pharmacy must be chosen for this refill. Please reopen the refill from your dashboard and pick one',
  invalid_dose: "the requested dose isn't available for this medication. Please reopen the refill from your dashboard",
  // §video-visit (BE #383): video was chosen but isn't offered for this visit
  // type/state any more (charge auto-refunded), or the value was malformed.
  video_unavailable: 'video visits are no longer available for this treatment in your state. Please go back and choose an online visit',
  invalid_visit_modality: 'the visit type you chose (online or video) was not recognised. Please go back and choose again',
};

export function describeSubmitFailure(error) {
  const data = (error && error.response && error.response.data) || {};

  let body;
  if (Array.isArray(data.errors) && data.errors.length) {
    body = `We couldn't submit your visit:\n\n• ${data.errors.join('\n• ')}`;
  } else if (data.belugaError) {
    body = `We couldn't submit your visit.\nError: ${data.belugaError} (status ${data.belugaStatus || 'unknown'})`;
  } else if (data.code && CODED_SUBMIT_ERRORS[data.code]) {
    body = `We couldn't submit your intake: ${CODED_SUBMIT_ERRORS[data.code]}${data.reason ? ` (${data.reason})` : ''}.`;
  } else {
    body = `There was an error submitting your form: ${data.msg || data.message || data.error || (error && error.message) || 'Please try again.'}`;
  }

  let refundLine;
  if (data.refunded === true) {
    refundLine = `\n\nGood news — your payment was refunded automatically${data.refundId ? ` (refund ID: ${data.refundId})` : ''}. No further action needed on the charge.`;
  } else if (data.refundReason) {
    refundLine = `\n\nWe couldn't auto-refund your payment (${data.refundReason}). Please contact support@fitpal.com and we'll process the refund manually.`;
  } else {
    refundLine = `\n\nIf you were charged, please contact support@fitpal.com so we can arrange a refund.`;
  }

  return `${body}${refundLine}`;
}

// Hex SHA-256 of a string via Web Crypto; null where it's unavailable (very
// old browsers, non-secure contexts, jsdom) so callers can omit the field.
export async function sha256Hex(text) {
  try {
    const subtle = typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle;
    if (!subtle || typeof TextEncoder === 'undefined') return null;
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(String(text)));
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    return null;
  }
}

// Beluga prefers height and weight as two separate Q/A entries (per Erika
// 2026-05-06 review of visit 86949787 which flagged the combined "What is
// your height and weight?" as a duplicated question). Returns 2 records when
// the answer has feet/inches/weight; otherwise null so callers fall through
// to their single-record default.
export function splitHeightWeightAnswer(answer) {
  if (!answer || typeof answer !== 'object' || answer.feet === undefined) return null;
  const heightRecord = {
    question: 'What is your height in feet and inches?',
    answer: `${answer.feet}'${answer.inches}"`,
    slide_id: 'height-feet-inches',
    rawAnswer: { feet: answer.feet, inches: answer.inches },
  };
  const weightRecord = {
    question: 'What is your weight in pounds?',
    answer: `${answer.weight} lbs`,
    slide_id: 'weight-pounds',
    rawAnswer: answer.bmi !== undefined
      ? { weight: answer.weight, bmi: answer.bmi }
      : { weight: answer.weight },
  };
  return [heightRecord, weightRecord];
}

// Render a multi-select answer (array of labels/values) as a bullet list so
// Beluga's clinical view shows each pick on its own line instead of a
// comma-joined run-on string. Per Erika 2026-05-12 review of AA visit
// 25652320 ("Anti-Aging, Boost energy" string → bullet points).
export function formatMultiSelectAnswer(answer) {
  if (!Array.isArray(answer)) return null;
  if (answer.length === 0) return '';
  return answer.map(item => `• ${item}`).join('\n');
}

// Render a multi-select-with-addons answer
// (`{ selectedEffects: ['fatigue', 'nausea'], additives: { fatigue: 'b12', nausea: 'b6' } }`)
// as a bullet list using the slide's option labels + per-effect additive
// labels. Falls back to raw values when the slide config is missing.
// Per Erika 2026-05-15 review of WLFU visit 66402450 — was leaking
// `JSON.stringify(answer)` into Beluga's A field.
export function formatAddonsAnswer(answer, slide) {
  if (!answer || !Array.isArray(answer.selectedEffects)) return null;
  if (answer.selectedEffects.length === 0) return '';
  const optionByValue = new Map(
    (slide?.options || []).map((opt) => [opt.value, opt])
  );
  return answer.selectedEffects
    .map((effectValue) => {
      const opt = optionByValue.get(effectValue);
      const label = opt?.label || effectValue;
      const additiveValue = answer.additives?.[effectValue];
      if (!additiveValue) return `• ${label}`;
      const additiveOpt = (opt?.additives || []).find(
        (a) => a.value === additiveValue
      );
      const additiveLabel = additiveOpt?.label || additiveValue;
      return `• ${label}\n  Additive: ${additiveLabel}`;
    })
    .join('\n');
}

// Compose the question text we send to the backend `answers[i].question` field
// so Beluga's Q/A entry carries the exact prompt the patient saw, including
// answer choices for select questions and the consent body for consent-dropdown.
//
// Per BACKEND_QUESTIONNAIRE_TEXT_PARITY_PRD.md §4.1, backend will use this
// string verbatim as Q{n}; until the backend ships, the field rides alongside
// `slide_id` and is harmless to FE clients on the existing dictionary path.
export function composeQaQuestionText(slide) {
  if (!slide) return '';
  const base = (slide.question || '').trim();
  // §questionnaire-parity: include the slide subtitle so clinically-meaningful
  // context (e.g. the TRT fertility warning on 'Which best describes your
  // situation?') reaches Beluga together with its question, not just the UI.
  // Beluga 2026-06-02 review of trt visit 11120290 flagged the missing
  // fertility text. Applies to all flows — subtitles are displayed
  // questionnaire content, so the visit record should carry them verbatim.
  const sub = (slide.subtitle || '').trim();

  const optionLabels = Array.isArray(slide.options)
    ? slide.options.map(o => (typeof o.label === 'string' ? o.label : '')).filter(Boolean)
    : [];
  const possibleAnswers = optionLabels.length ? `POSSIBLE ANSWERS: ${optionLabels.join('; ')}` : '';

  switch (slide.type) {
    case 'single-select':
    case 'single-select-prefilled':
    case 'multi-select':
    case 'multi-select-with-addons': {
      const parts = [base];
      if (sub) parts.push(sub);
      if (possibleAnswers) parts.push(possibleAnswers);
      return parts.join(' ');
    }
    case 'consent-dropdown': {
      const body = (slide.consentText || '').trim();
      const parts = [base];
      if (sub) parts.push(sub);
      if (body) parts.push(body);
      if (possibleAnswers) parts.push(possibleAnswers);
      return parts.join('\n\n');
    }
    case 'info-text':
      return (slide.infoText || base || '').trim();
    default: {
      const parts = [base];
      if (sub) parts.push(sub);
      return parts.join(' ');
    }
  }
}

